/**
 * Armazenamento do aparelho da Área do PCP (ADR-0087, decisão 7): chave →
 * valor em IndexedDB, uma base por unidade e usuário. A API está em outra
 * origem e o service worker não a intercepta, então o cache e a fila de
 * eventos moram aqui, no próprio app.
 *
 * Duas implementações com a mesma interface (`ler`, `gravar`, `apagar`):
 * IndexedDB no navegador e memória nos testes (ou quando o navegador nega o
 * IndexedDB, como em janela anônima de alguns navegadores).
 */
import { aoLimparDadosDoTenant } from "../../../utils/tenantStorage";

export const PREFIXO_DA_BASE = "pharuerp-area-pcp";
const LOJA = "kv";
export const CHAVE_PACOTE = "pacote";
export const CHAVE_FILA = "fila";

export function nomeDaBase(unidade, usuario) {
  return `${PREFIXO_DA_BASE}:${unidade}:${usuario}`;
}

const copia = (valor) => (valor === undefined ? null : JSON.parse(JSON.stringify(valor)));

export function criarArmazemMemoria() {
  const mapa = new Map();
  return {
    ler: async (chave) => copia(mapa.get(chave)),
    gravar: async (chave, valor) => {
      mapa.set(chave, copia(valor));
    },
    apagar: async (chave) => {
      mapa.delete(chave);
    },
  };
}

function pedido(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function abrirBase(nome) {
  const req = window.indexedDB.open(nome, 1);
  req.onupgradeneeded = () => {
    if (!req.result.objectStoreNames.contains(LOJA)) req.result.createObjectStore(LOJA);
  };
  return pedido(req);
}

export function criarArmazemIndexedDb(nome) {
  let conexao = null;
  const base = () => {
    conexao = conexao || abrirBase(nome);
    return conexao;
  };
  async function loja(modo) {
    const db = await base();
    return db.transaction(LOJA, modo).objectStore(LOJA);
  }
  return {
    ler: async (chave) => copia(await pedido((await loja("readonly")).get(chave))),
    gravar: async (chave, valor) => {
      await pedido((await loja("readwrite")).put(copia(valor), chave));
    },
    apagar: async (chave) => {
      await pedido((await loja("readwrite")).delete(chave));
    },
  };
}

/** IndexedDB quando o navegador deixa; memória quando não (o app segue online). */
export function criarArmazem(nome) {
  if (typeof window === "undefined" || !window.indexedDB) return criarArmazemMemoria();
  return criarArmazemIndexedDb(nome);
}

/**
 * Logout em aparelho compartilhado: apaga o que é conteúdo (manuais e
 * execuções com nome de cliente), mas guarda a fila de toques ainda não
 * enviados — ela só tem ids e motivos, e sai na próxima entrada do mesmo
 * usuário em vez de se perder.
 */
export async function limparPacotesDoAparelho() {
  if (typeof window === "undefined" || !window.indexedDB?.databases) return;
  const bases = await window.indexedDB.databases();
  await Promise.all(
    bases
      .filter((b) => b.name?.startsWith(`${PREFIXO_DA_BASE}:`))
      .map((b) => criarArmazemIndexedDb(b.name).apagar(CHAVE_PACOTE)),
  );
}

aoLimparDadosDoTenant(() => {
  limparPacotesDoAparelho().catch(() => {
    // Best-effort: sem IndexedDB não há pacote a apagar.
  });
});
